import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import PropTypes from "prop-types";

const PsqiCardResults = ({
  title,
  data,
  dataKey,
  strokeColor = "#29ade6",
  height = 300,
}) => {
  // Filtrar los datos para eliminar valores vacíos
  const validData = data.map(item => ({
    ...item,
    [dataKey]: item[dataKey] === '' ? null : item[dataKey]
  }));

  const getColor = (value) => {
    if (value === null) return "#29ade6";
    
    if (dataKey === "puntuacionGlobal") {
      return value >= 5 ? "#ff4600" : "#29ade6"; // Azul si ≤ 4, rojo si ≥ 5
    }
    // Escala de colores para otros casos
    switch (value) {
      case 0:
        return "#29ade6"; // Azul
      case 1:
        return "#ffd700"; // Amarillo
      case 2:
        return "#ffa500"; // Naranja
      case 3:
        return "#ff4600"; // Rojo
      default:
        return "#29ade6"; // Azul por defecto
    }
  };

  // 1. Revisar si todos tienen el mismo valor (excluyendo valores nulos)
  const allValues = validData.map((d) => d[dataKey]).filter(value => value !== null);
  const uniqueValues = new Set(allValues);
  const isAllSame = uniqueValues.size === 1;

  return (
    <div className="anali-el">
      <p className="anali-title">{title}</p>
      <div className="anali-result-container">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart
            data={validData}
            margin={{ top: 50, right: 0, left: -30, bottom: 0 }}
            connectNulls={false}
          >
            <defs>
              {!isAllSame && (
                <linearGradient id={`dynamicGradient-${dataKey}`} x1="0" y1="0" x2="1" y2="0">
                  {validData.map((point, index) => {
                    const value = point[dataKey];
                    if (value === null) return null;
                    const color = getColor(value);
                    const position = (index / (validData.length - 1)) * 100;
                    return (
                      <stop
                        key={`stop-${index}`}
                        offset={`${position}%`}
                        stopColor={color}
                      />
                    );
                  })}
                </linearGradient>
              )}
            </defs>

            <YAxis
              type="number"
              domain={dataKey === "puntuacionGlobal" ? [0, 20] : [0, 3]}
              ticks={dataKey !== "puntuacionGlobal" ? [0, 1, 2, 3] : undefined}
              axisLine={{ strokeWidth: 0 }}
              tickLine={{
                stroke: "#777777",
                strokeWidth: 1,
                length: 6,
              }}
              tick={{
                fontSize: 12,
                fill: "#777777",
              }}
            />

            <XAxis
              axisLine={{ strokeWidth: 0 }}
              dataKey="fecha"
              padding={{ left: 50, right: 40 }}
              dy={10}
              tick={{
                fontSize: 12,
                fill: "#777777",
              }}
            />
            <Tooltip />
            <Line
              key={dataKey}
              type="monotone"
              dataKey={dataKey}
              strokeWidth={8}
              stroke={
                isAllSame
                  ? getColor(validData.find(d => d[dataKey] !== null)?.[dataKey]) // un solo color
                  : `url(#dynamicGradient-${dataKey})` // gradiente
              }
              label={(props) => {
                const { x, y, value, index } = props;
                if (value === null) return null;
                const color = getColor(value);
                return (
                  <text
                    key={`label-${index}`}
                    x={x}
                    y={y - 20}
                    fontSize={36}
                    fontWeight="bold"
                    textAnchor="middle"
                    fill={color}
                    className="recharts-text recharts-label"
                  >
                    <tspan x={x} dy="0em" key={`tspan-${index}`}>
                      {value}
                    </tspan>
                  </text>
                );
              }}
              dot={(props) => {
                const { cx, cy, value, index } = props;
                if (value === null) return null;
                const color = getColor(value);
                return (
                  <circle
                    key={`dot-${index}`}
                    cx={cx}
                    cy={cy}
                    r={6}
                    fill={color}
                    stroke="#fff"
                    strokeWidth={4}
                  />
                );
              }}
              activeDot={(props) => {
                const { cx, cy, value, index } = props;
                if (value === null) return null;
                const color = getColor(value);
                return (
                  <g
                    className="recharts-layer recharts-active-dot"
                    key={`activedot-${index}`}
                  >
                    <circle
                      cx={cx}
                      cy={cy}
                      r={6}
                      fill={color}
                      stroke="#fff"
                      strokeWidth={6}
                      className="recharts-dot"
                    />
                  </g>
                );
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

PsqiCardResults.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  dataKey: PropTypes.string.isRequired,
  strokeColor: PropTypes.string,
};

export default PsqiCardResults;
